import {
  makeStyles,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import EditIcon from "@material-ui/icons/Edit";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import Table from "@material-ui/core/Table";
import TablePagination from "@material-ui/core/TablePagination";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeGetFilteredPayPeriods } from "../../../../../../selectors/payPeriodSelectors";
import { getCurrentIndividual } from "../../../../../../selectors/individualSelectors";
import { getEmployerCanShowLedgerDispute } from "../../../../../../selectors/ledgerDisputeBetaSelectors";
import { BilledAmountCellContent } from "../../../../../employer/Billing/Temp/InvoicePeriodItem/PayPeriods/PayPeriodsTableRow";
import { formatter } from "../../../../../recruiter/Candidates/actions/modals/utils";
import { AdditionalBilling, NonStandardHours, StandardHours } from "../../../../PayPeriods/tableColumns";
import ActionMenu from "../../../../../General/ActionMenu";
import { openModal } from "../../../../../../actions/shared/uiActions";
import EmployerSubmitHoursModal from "../../../../../employer/PayPeriods/modals/SubmitHoursModal";
import RecruiterSubmitHoursModal from "../../../../../recruiter/Candidates/actions/modals/SubmitHoursModal";
import ReportTimesheetCorrectionModal from "../../../../../recruiter/PayPeriods/modals/ReportTimesheetCorrectionModal";
import DisputePayPeriodModal from "../../../../../shared/PayPeriods/modals/DisputePayPeriodModal";

const useStyles = makeStyles((theme) => ({
  container: {
    margin: `0px ${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(
      2
    )}px`,
  },
  header: {
    backgroundColor: "#e3e9e9",
    "& th": {
      fontWeight: "bold",
      color: "#37474F",
    },
  },
  body: {
    "& tr:nth-child(even)": {
      backgroundColor: "#f4f4f4",
    },
  },
  standardCell: {
    color: "#37474F",
  },
  alertCell: {
    color: "#F44336",
  },
  boldedCell: {
    fontWeight: "bold",
    color: "#37474F",
  },
  paginationSpacer: {
    flex: "inherit",
  },
  menuPaper: {
    width: 200,
  },
}));

const payoutStatus = (
  isEmployer,
  {
    approvedAt,
    collectionAt,
    payoutStatus: payoutStatusValue,
    disputedAt,
    status,
    correctionStatus,
    invoiceFinalizedAt,
  },
  canShowCorrectionStatus
) => {
  if (disputedAt) return "Disputed";

  if (canShowCorrectionStatus && correctionStatus) {
    return `${correctionStatus}*`;
  }

  if (status) {
    return status;
  }

  if (isEmployer) {
    if (collectionAt !== null) return "Paid";
    if (invoiceFinalizedAt) return "Invoiced";
  } else if (payoutStatusValue === "processed") return "Paid";

  return approvedAt ? "Approved" : "Submitted";
};

const ROWS_PER_PAGE = 5;

const REPORT_CORRECTION = "report_correction";
const DISPUTE = "dispute";
const MANAGE_TIMESHEET = "manage_timesheet";

function calculateActionMenuPermissions(contractAttributes, payPeriodAttributes, isRecruiter) {
  const {
    isContractManager,
    isResponsibleSubmittingParty,
    isWfmContract,
  } = contractAttributes || {};

  const {
    invoiceNumber,
    invoiceFinalizedAt,
    submittedAt,
  } = payPeriodAttributes || {};

  if (!contractAttributes || !isContractManager || !isResponsibleSubmittingParty) {
    return {
      canShowActionMenu: false,
      canReportCorrection: false,
      canDispute: false,
      canManageTimesheet: false,
    };
  }

  const isWfm = Boolean(isWfmContract);
  const invoiced = Boolean(invoiceNumber || invoiceFinalizedAt);
  const submitted = Boolean(submittedAt);

  const canShowActionMenu = isRecruiter;

  const canReportCorrection =
    (isWfm && isRecruiter && invoiced) || (!isWfm && !isRecruiter && invoiced);

  const canManageTimesheet = !isWfm && isRecruiter;
  const canDispute = submitted && !invoiced && !canManageTimesheet;
  return {
    canShowActionMenu,
    canReportCorrection,
    canDispute,
    canManageTimesheet,
  };
}

function TimesheetActionMenu({ payPeriod, contract, candidateId, isRecruiter }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const SubmitHoursModal = isRecruiter ? RecruiterSubmitHoursModal : EmployerSubmitHoursModal;
  const permissions = useMemo(
    () => calculateActionMenuPermissions(contract.attributes, payPeriod.attributes, isRecruiter),
    [contract.attributes, payPeriod.attributes, isRecruiter]
  );

  const actions = useMemo(() => {
    const actionList = [];

    if (permissions.canReportCorrection) {
      actionList.push({
        name: REPORT_CORRECTION,
        icon: <EditIcon />,
        isVisible: true,
        label: "Report Correction",
      });
    }

    if (permissions.canDispute) {
      actionList.push({
        name: DISPUTE,
        icon: <ReportProblemIcon />,
        isVisible: true,
        label: "Dispute",
      });
    }

    if (permissions.canManageTimesheet) {
      actionList.push({
        name: MANAGE_TIMESHEET,
        icon: <EditIcon />,
        isVisible: true,
        label: "Manage Timesheet",
      });
    }

    return actionList;
  }, [permissions]);

  const handleActionSelection = (action) => {
    switch (action) {
      case REPORT_CORRECTION:
        dispatch(
          openModal(
            <ReportTimesheetCorrectionModal payPeriodId={payPeriod.id} />
          )
        );
        break;
      case DISPUTE:
        dispatch(
          openModal(
            <DisputePayPeriodModal
              payPeriodId={payPeriod.id}
              editedAt={payPeriod.attributes.editedAt}
            />
          )
        );
        break;
      case MANAGE_TIMESHEET:
        dispatch(
          openModal(
            <SubmitHoursModal
              candidateId={candidateId}
              initialPayPeriodId={payPeriod.id}
              title="Manage Timesheet"
            />
          )
        );
        break;
      default:
        break;
    }
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <ActionMenu
      actions={actions}
      handleActionSelection={handleActionSelection}
      menuClasses={{ paper: classes.menuPaper }}
    />
  );
}

function EmployerTableContent({ windowedPayPeriods, contract, candidateId }) {
  const classes = useStyles();

  return (
    <Table>
      <TableHead classes={{ root: classes.header }}>
        <TableCell>Week Start</TableCell>
        <TableCell>Week End</TableCell>
        <TableCell align="center">Std. Hrs.</TableCell>
        <TableCell align="center">Non-Std. Hrs.</TableCell>
        <TableCell align="center">Total Bill</TableCell>
        <TableCell>Status</TableCell>
      </TableHead>
      <TableBody classes={{ root: classes.body }}>
        {map(windowedPayPeriods, (payPeriod) => (
          <TableRow key={payPeriod.id}>
            <TableCell classes={{ root: classes.standardCell }}>
              {moment(payPeriod.attributes.startDate).format("MMM Do, YYYY")}
            </TableCell>
            <TableCell classes={{ root: classes.standardCell }}>
              {moment(payPeriod.attributes.endDate).format("MMM Do, YYYY")}
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <StandardHours {...payPeriod.attributes} />
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <NonStandardHours {...payPeriod.attributes} />
            </TableCell>
            <TableCell align="center" classes={{ root: classes.standardCell }}>
              <BilledAmountCellContent {...payPeriod.attributes} />
            </TableCell>
            <TableCell classes={{ root: classes.standardCell }}>
              {payoutStatus(true, payPeriod.attributes)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RecruiterTableContent({ windowedPayPeriods, contract, candidateId, isRecruiter }) {
  const classes = useStyles();

  return (
    <Table>
      <TableHead classes={{ root: classes.header }}>
        <TableCell>Week Start</TableCell>
        <TableCell>Week End</TableCell>
        <TableCell align="center">Std. Hrs.</TableCell>
        <TableCell align="center">Non-Std. Hrs.</TableCell>
        <TableCell align="center">Add.</TableCell>
        <TableCell align="center">Earned</TableCell>
        <TableCell>Status</TableCell>
        <TableCell align="center">Actions</TableCell>
      </TableHead>
      <TableBody classes={{ root: classes.body }}>
        {map(windowedPayPeriods, (payPeriod) => (
          <TableRow key={payPeriod.id}>
            <TableCell classes={{ root: classes.standardCell }}>
              {moment.parseZone(payPeriod.attributes.startDate).format("MMM Do, YYYY")}
            </TableCell>
            <TableCell classes={{ root: classes.standardCell }}>
              {moment.parseZone(payPeriod.attributes.endDate).format("MMM Do, YYYY")}
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <StandardHours {...payPeriod.attributes} />
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <NonStandardHours {...payPeriod.attributes} />
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <AdditionalBilling {...payPeriod.attributes} />
            </TableCell>
            <TableCell
              align="center"
              classes={{ root: classes.standardCell }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                {formatter.format(payPeriod.attributes.recruiterPayoutAmount)}
                {payPeriod.attributes.documentUrl && (
                  <a
                    href={payPeriod.attributes.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", alignItems: "center", color: "#008DAE" }}
                  >
                    <AttachFileIcon style={{ fontSize: "18px" }} />
                  </a>
                )}
              </div>
            </TableCell>
            <TableCell classes={{ root: classes.standardCell }}>
              {payoutStatus(false, payPeriod.attributes)}
            </TableCell>
            <TableCell align="center" classes={{ root: classes.standardCell }}>
              {calculateActionMenuPermissions(contract.attributes, payPeriod.attributes, isRecruiter)
                .canShowActionMenu && (
                  <TimesheetActionMenu
                    payPeriod={payPeriod}
                    contract={contract}
                    candidateId={candidateId}
                    isRecruiter={isRecruiter}
                  />
                )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PayPeriodsTable({ contract, isEmployer, candidateId }) {
  const [tablePage, setTablePage] = useState(0);
  const getPayPeriods = useMemo(makeGetFilteredPayPeriods, []);
  const currentIndividual = useSelector(getCurrentIndividual);
  const isRecruiter = !currentIndividual.employer;

  const payPeriods = useSelector((state) => getPayPeriods(
    state,
    { contractId: contract.id, submittedHistory: true }
  ));

  const orderedPayPeriods = useMemo(
    () => orderBy(payPeriods, [({ attributes }) => attributes.startDate], ["desc"]),
    [payPeriods]
  );

  const windowedPayPeriods = useMemo(() => {
    const startIdx = tablePage * ROWS_PER_PAGE;
    const endIdx = startIdx + ROWS_PER_PAGE;

    return orderedPayPeriods.slice(startIdx, endIdx);
  }, [tablePage, orderedPayPeriods]);

  const classes = useStyles();
  const TableContentComponent = isEmployer ? EmployerTableContent : RecruiterTableContent;

  return (
    <div data-cy="pay-periods-table">
      <TableContainer component={Paper} classes={{ root: classes.container }}>
        {React.createElement(TableContentComponent, {
          windowedPayPeriods,
          contract,
          candidateId,
          isRecruiter,
        })}
        <TablePagination
          classes={{
            spacer: classes.paginationSpacer,
            root: classes.boldedCell,
          }}
          component="div"
          count={payPeriods.length}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          page={tablePage}
          onChangePage={(_event, newPage) => setTablePage(newPage)}
        />
      </TableContainer>
    </div>
  );
}

export default PayPeriodsTable;
